import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Fade,
  Slide,
  Chip,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  HourglassEmpty,
  Sync
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * ProgressIndicator Component
 *
 * Displays real-time progress updates at the center bottom of the screen
 * Used for login and logout operations to show what's happening
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the indicator is visible
 * @param {string} props.currentStep - Current step message
 * @param {Array<string>} props.completedSteps - Array of completed step messages
 * @param {number} props.progress - Progress percentage (0-100)
 * @param {string} props.status - Status: 'loading' | 'success' | 'error'
 */
const ProgressIndicator = ({
  open = false,
  currentStep = '',
  completedSteps = [],
  progress = 0,
  status = 'loading'
}) => {
  const theme = useTheme();

  if (!open) return null;

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
      case 'error':
        return <HourglassEmpty sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
      default:
        return <Sync sx={{ color: theme.palette.primary.main, fontSize: 20, animation: 'spin 1s linear infinite' }} />;
    }
  };

  return (
    <Slide direction="up" in={open} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          width: '90%',
          maxWidth: 600,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 3,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.98)'
              : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          {/* Current Step */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Box
              sx={{
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              {getStatusIcon()}
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.primary,
                flex: 1
              }}
            >
              {currentStep}
            </Typography>
          </Stack>

          {/* Progress Bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mb: 2,
              height: 6,
              borderRadius: 3,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: status === 'error'
                  ? theme.palette.error.main
                  : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }
            }}
          />

          {/* Completed Steps */}
          {completedSteps.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {completedSteps.slice(-3).map((step, index) => (
                <Fade in key={index} timeout={300}>
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 16 }} />}
                    label={step}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(76, 175, 80, 0.2)'
                        : 'rgba(76, 175, 80, 0.1)',
                      color: theme.palette.success.main,
                      fontSize: '0.75rem',
                      '& .MuiChip-icon': {
                        color: theme.palette.success.main
                      }
                    }}
                  />
                </Fade>
              ))}
            </Stack>
          )}

          {/* Progress Percentage */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 1,
              color: theme.palette.text.secondary,
              fontWeight: 500
            }}
          >
            {Math.round(progress)}% Complete
          </Typography>
        </Paper>
      </Box>
    </Slide>
  );
};

export default ProgressIndicator;
